import React, { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CalendarEvent } from '../api/tournament/fixturesService';
import './MatchCalendar.css';

interface MatchCalendarProps {
  calendarEvents: CalendarEvent[];
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

const MatchCalendar: React.FC<MatchCalendarProps> = ({
  calendarEvents,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate,
  maxDate,
  disabled = false,
}) => {
  // Create a Set of dates that have matches for quick lookup
  // Normalize dates to YYYY-MM-DD format for comparison
  const matchDatesSet = new Set(calendarEvents
    .filter(event => event && event.date) // Filter out invalid events
    .map(event => {
      // Ensure date is in YYYY-MM-DD format
      const dateStr = typeof event.date === 'string' ? event.date : String(event.date);
      // Handle both ISO format (with 'T') and plain date strings
      return dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    })
  );
  const matchDatesMap = new Map<string, number>();
  calendarEvents
    .filter(event => event && event.date) // Filter out invalid events
    .forEach(event => {
      const dateStr = typeof event.date === 'string' ? event.date : String(event.date);
      // Handle both ISO format (with 'T') and plain date strings
      const normalizedDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      matchDatesMap.set(normalizedDate, event.count || 0);
    });

  // Check if a date has matches
  const hasMatches = (date: Date): boolean => {
    // Convert date to YYYY-MM-DD format for comparison
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return matchDatesSet.has(dateStr);
  };

  // Get match count for a date
  const getMatchCount = (date: Date): number => {
    // Convert date to YYYY-MM-DD format for comparison
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return matchDatesMap.get(dateStr) || 0;
  };

  // Custom day component to show dots for dates with matches
  const renderDayContents = (day: number, date: Date) => {
    const hasMatch = hasMatches(date);
    const matchCount = hasMatch ? getMatchCount(date) : 0;
    const isSelected = 
      (startDate && date.toDateString() === startDate.toDateString()) ||
      (endDate && date.toDateString() === endDate.toDateString()) ||
      (startDate && endDate && date >= startDate && date <= endDate);

    return (
      <div className={`match-calendar-day ${isSelected ? 'selected' : ''} ${hasMatch ? 'has-matches' : ''}`}>
        <span className="day-number">{day}</span>
        {hasMatch && (
          <div className="match-indicator" title={`${matchCount} match${matchCount > 1 ? 'es' : ''} on this date`}>
            <span className="match-dot"></span>
          </div>
        )}
      </div>
    );
  };

  // Handle date selection with range support
  // react-datepicker with selectsRange returns [startDate, endDate] or a single date
  const handleDateChange = (dates: [Date | null, Date | null] | Date | null) => {
    if (!dates) {
      onStartDateChange(null);
      onEndDateChange(null);
      return;
    }

    if (Array.isArray(dates)) {
      onStartDateChange(dates[0]);
      onEndDateChange(dates[1] || null);
    } else {
      // Single date selected
      onStartDateChange(dates);
      onEndDateChange(null);
    }
  };

  return (
    <div className="match-calendar-container">
      <div className="match-calendar-wrapper">
        <DatePicker
          selected={startDate}
          onChange={handleDateChange}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          inline
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          renderDayContents={renderDayContents}
          calendarClassName="match-calendar"
          dayClassName={(date) => {
            if (hasMatches(date)) {
              return 'has-matches-day';
            }
            return '';
          }}
        />
      </div>
      <div className="match-calendar-legend">
        <div className="legend-item">
          <span className="legend-dot"></span>
          <span>Dates with matches</span>
        </div>
        <div className="legend-item">
          <span className="legend-selected"></span>
          <span>Selected date range</span>
        </div>
      </div>
      {(startDate || endDate) && (
        <div className="selected-dates-info">
          {startDate && (
            <div className="date-info">
              <strong>Start:</strong> {startDate.toLocaleDateString()}
            </div>
          )}
          {endDate && (
            <div className="date-info">
              <strong>End:</strong> {endDate.toLocaleDateString()}
            </div>
          )}
          {startDate && !endDate && (
            <div className="date-hint">Select an end date to complete the range</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchCalendar;

